import { type FC, useCallback } from 'react';
import styled, { keyframes } from 'styled-components';

import { type ThemeMode, useTheme } from '../../../context/theme';

interface ThemeToggleProps {
	showLabel?: boolean;
	size?: 'small' | 'medium' | 'large';
	className?: string;
}

// Sun icon
const SunIcon: FC<{ className?: string }> = ({ className }) => (
	<svg
		className={className}
		width='20'
		height='20'
		viewBox='0 0 24 24'
		fill='none'
		xmlns='http://www.w3.org/2000/svg'
		aria-hidden='true'
	>
		<circle
			cx='12'
			cy='12'
			r='5'
			stroke='currentColor'
			strokeWidth='2'
		/>
		<path
			d='M12 2V4M12 20V22M4 12H2M22 12H20M5.64 5.64L4.22 4.22M19.78 19.78L18.36 18.36M5.64 18.36L4.22 19.78M19.78 4.22L18.36 5.64'
			stroke='currentColor'
			strokeWidth='2'
			strokeLinecap='round'
		/>
	</svg>
);

// Moon icon
const MoonIcon: FC<{ className?: string }> = ({ className }) => (
	<svg
		className={className}
		width='20'
		height='20'
		viewBox='0 0 24 24'
		fill='none'
		xmlns='http://www.w3.org/2000/svg'
		aria-hidden='true'
	>
		<path
			d='M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z'
			stroke='currentColor'
			strokeWidth='2'
			strokeLinecap='round'
			strokeLinejoin='round'
		/>
	</svg>
);

// System icon
const SystemIcon: FC<{ className?: string }> = ({ className }) => (
	<svg
		className={className}
		width='20'
		height='20'
		viewBox='0 0 24 24'
		fill='none'
		xmlns='http://www.w3.org/2000/svg'
		aria-hidden='true'
	>
		<rect
			x='2'
			y='3'
			width='20'
			height='14'
			rx='2'
			stroke='currentColor'
			strokeWidth='2'
		/>
		<path
			d='M8 21H16M12 17V21'
			stroke='currentColor'
			strokeWidth='2'
			strokeLinecap='round'
		/>
	</svg>
);

const fadeScale = keyframes`
	0% {
		opacity: 0;
		transform: scale(0.8);
	}
	100% {
		opacity: 1;
		transform: scale(1);
	}
`;

const sizeMap = {
	small: { button: '32px', icon: '16px' },
	medium: { button: '40px', icon: '20px' },
	large: { button: '48px', icon: '24px' },
};

const ToggleButton = styled.button<{ $size: 'small' | 'medium' | 'large' }>`
	display: flex;
	align-items: center;
	justify-content: center;
	width: ${({ $size }) => sizeMap[$size].button};
	height: ${({ $size }) => sizeMap[$size].button};
	border-radius: var(--radius-lg, 8px);
	background: var(--bg-tertiary, #f5f5f5);
	border: 1px solid var(--border-primary, #e0e0e0);
	color: var(--text-secondary, #616161);
	cursor: pointer;
	transition:
		background-color var(--transition-fast, 100ms) ease,
		border-color var(--transition-fast, 100ms) ease,
		transform var(--transition-fast, 100ms) ease,
		box-shadow var(--transition-fast, 100ms) ease;

	svg {
		width: ${({ $size }) => sizeMap[$size].icon};
		height: ${({ $size }) => sizeMap[$size].icon};
		transition: transform var(--transition-normal, 200ms) ease;
	}

	&:hover {
		background: var(--bg-hover, #eeeeee);
		border-color: var(--border-secondary, #d0d0d0);
		transform: translateY(-1px);
		box-shadow: var(--shadow-sm, 0 2px 4px rgba(0, 0, 0, 0.1));

		svg {
			transform: rotate(15deg);
		}
	}

	&:active {
		transform: translateY(0) scale(0.95);
	}

	&:focus-visible {
		outline: 2px solid var(--color-primary, #1b6b4a);
		outline-offset: 2px;
	}

	&.sun svg {
		color: var(--color-accent, #d4920b);
	}

	&.moon svg {
		color: var(--color-primary, #1b6b4a);
	}

	.icon-enter {
		animation: ${fadeScale} var(--transition-normal, 200ms) ease forwards;
	}
`;

const ToggleWrapper = styled.div`
	display: flex;
	align-items: center;
	gap: var(--spacing-2, 0.5rem);
`;

const Label = styled.span`
	font-size: var(--font-size-sm, 0.75rem);
	color: var(--text-secondary, #616161);
	text-transform: capitalize;
`;

// Simple toggle that switches between light/dark
export const ThemeToggle: FC<ThemeToggleProps> = ({ showLabel = false, size = 'medium', className }) => {
	const { resolvedTheme, toggleTheme } = useTheme();
	const isDark = resolvedTheme === 'dark';

	return (
		<ToggleWrapper className={className}>
			<ToggleButton
				type='button'
				onClick={toggleTheme}
				$size={size}
				className={isDark ? 'moon' : 'sun'}
				aria-label={`Switch to ${isDark ? 'light' : 'dark'} mode`}
				title={`Switch to ${isDark ? 'light' : 'dark'} mode`}
			>
				{isDark ? <MoonIcon className='icon-enter' /> : <SunIcon className='icon-enter' />}
			</ToggleButton>
			{showLabel && <Label>{isDark ? 'Dark' : 'Light'} mode</Label>}
		</ToggleWrapper>
	);
};

// Advanced toggle with light/dark/system options
const SegmentedToggle = styled.div<{ $size: 'small' | 'medium' | 'large' }>`
	display: flex;
	align-items: center;
	background: var(--bg-tertiary, #f5f5f5);
	border-radius: var(--radius-lg, 8px);
	padding: 4px;
	border: 1px solid var(--border-primary, #e0e0e0);
	gap: 2px;
`;

const SegmentButton = styled.button<{ $active: boolean; $size: 'small' | 'medium' | 'large' }>`
	display: flex;
	align-items: center;
	justify-content: center;
	width: ${({ $size }) => sizeMap[$size].button};
	height: calc(${({ $size }) => sizeMap[$size].button} - 8px);
	border-radius: var(--radius-md, 6px);
	background: ${({ $active }) => ($active ? 'var(--bg-primary, #ffffff)' : 'transparent')};
	border: none;
	color: ${({ $active }) => ($active ? 'var(--text-primary, #212121)' : 'var(--text-muted, #9e9e9e)')};
	cursor: pointer;
	transition:
		background-color var(--transition-fast, 100ms) ease,
		color var(--transition-fast, 100ms) ease,
		box-shadow var(--transition-fast, 100ms) ease;
	box-shadow: ${({ $active }) => ($active ? 'var(--shadow-sm, 0 1px 3px rgba(0, 0, 0, 0.1))' : 'none')};

	svg {
		width: ${({ $size }) => `calc(${sizeMap[$size].icon} - 4px)`};
		height: ${({ $size }) => `calc(${sizeMap[$size].icon} - 4px)`};
	}

	&:hover:not(:disabled) {
		color: var(--text-primary, #212121);
		background: ${({ $active }) => ($active ? 'var(--bg-primary, #ffffff)' : 'var(--bg-hover, #eeeeee)')};
	}

	&:focus-visible {
		outline: 2px solid var(--color-primary, #1b6b4a);
		outline-offset: 1px;
	}
`;

interface ThemeSwitcherProps {
	size?: 'small' | 'medium' | 'large';
	className?: string;
}

export const ThemeSwitcher: FC<ThemeSwitcherProps> = ({ size = 'medium', className }) => {
	const { theme, setTheme } = useTheme();

	const handleThemeChange = useCallback(
		(newTheme: ThemeMode) => {
			setTheme(newTheme);
		},
		[setTheme]
	);

	return (
		<SegmentedToggle
			$size={size}
			className={className}
			role='radiogroup'
			aria-label='Theme selection'
		>
			<SegmentButton
				type='button'
				$active={theme === 'light'}
				$size={size}
				onClick={() => handleThemeChange('light')}
				aria-label='Light theme'
				aria-checked={theme === 'light'}
				role='radio'
				title='Light theme'
			>
				<SunIcon />
			</SegmentButton>
			<SegmentButton
				type='button'
				$active={theme === 'system'}
				$size={size}
				onClick={() => handleThemeChange('system')}
				aria-label='System theme'
				aria-checked={theme === 'system'}
				role='radio'
				title='System theme'
			>
				<SystemIcon />
			</SegmentButton>
			<SegmentButton
				type='button'
				$active={theme === 'dark'}
				$size={size}
				onClick={() => handleThemeChange('dark')}
				aria-label='Dark theme'
				aria-checked={theme === 'dark'}
				role='radio'
				title='Dark theme'
			>
				<MoonIcon />
			</SegmentButton>
		</SegmentedToggle>
	);
};

export default ThemeToggle;
